/**
 * WebSocket Message Types
 * Structures for real-time data received via Scaledrone WebSocket
 */

import type { EncoderSampleData } from './triage';

// ============================================================================
// Event Status Messages (from Scaledrone)
// ============================================================================

/**
 * Full event object received via WebSocket when event status changes
 * Based on actual payloads from production API
 */
export interface WebSocketEventData {
  eventID: string;
  clientID: string;
  customID: string | null;
  startDateTime: string;
  title: string;
  description: string | null;
  eventStatus: string; // "starting" | "live" | "on break" | "stopping" | "available" | "new" | etc.
  videoThumbnail: string | null;
  private: boolean;
  autoStart: boolean;
  autoStop: boolean;
  preroll: number;
  categories: any[];
  keywords: any[];
  technicians: any[];
  locationName: string | null;
  facebookLive: any;
  transcripts: any;
  isChatEnabled: boolean;
  estRuntime: number;
  breakOverride: boolean;
  notes: string | null;
  eventMetaMarkers: any[];
  playerPref: any;
  assetCount: number;
  assignedEncoder: string;
  encoderDetails: {
    simpleName: string;
    online: boolean;
    lastComs: string;
    previewURI: string;
  };
  mediaAssets: any[];
  streamingURIs: {
    main: string;
    backup: string | null;
  };
  captionPath: string | null;
  indexItems: any[];
  attendees: any[];
  VAST: boolean;
  liveToBreak: boolean;
  liveStats?: {
    encoderID: string;
    liveID: string;
    startDate: string;
    status: string;
    runtime: string;
  };
  liveLogTimeInfo?: {
    liveStreams: Array<{
      liveID: string;
      startDateUTC: string;
      endDateUTC: string | null;
      status: string;
      estRuntime: string;
    }>;
    contiguityGap: number;
  };
  liveCaptions: any;
  liveAiCaptions: {
    isEnabled: boolean;
    statsInformation: any;
  };
  chat: any;
  restrictedDomains: any;
  customData: any;
}

/**
 * Scaledrone message wrapper
 */
export interface ScaledroneMessage {
  id: string; // Scaledrone message ID
  timestamp: number; // Unix timestamp
  type: string; // "publish"
  room: string; // clientID
  message: {
    action: {
      type: string; // "start" | "pause" | "update" | "stopping" | "stop"
      method: string; // "events" for event-related messages
      timestamp: string;
    };
    errors: {
      hasError: boolean;
      message: string | null;
    };
    data: WebSocketEventData;
    meta: any | null;
  };
}

// ============================================================================
// Event Status Constants
// ============================================================================

/**
 * Event statuses that should appear in "Live Streams" section
 */
export const LIVE_EVENT_STATUSES = ['starting', 'live', 'on break', 'stopping', 'disconnected'] as const;

/**
 * Event status when stream has completed
 */
export const COMPLETED_EVENT_STATUS = 'available' as const;

/**
 * Event status when first created (ignore until goes live)
 */
export const NEW_EVENT_STATUS = 'new' as const;

/**
 * Type guard to check if event status is "live"
 */
export function isLiveEventStatus(status: string): boolean {
  return LIVE_EVENT_STATUSES.includes(status as any);
}

/**
 * Type guard to check if event status is "completed"
 */
export function isCompletedEventStatus(status: string): boolean {
  return status === COMPLETED_EVENT_STATUS;
}

/**
 * Type guard to check if message is an event status message
 */
export function isEventMessage(message: any): message is ScaledroneMessage {
  return (
    message?.message?.action?.method === 'events' &&
    message?.message?.data?.eventID &&
    message?.message?.data?.eventStatus
  );
}

// ============================================================================
// Encoder WSS Messages (Connect/Disconnect)
// ============================================================================

/**
 * Encoder data received via WebSocket on connect/disconnect
 */
export interface EncoderWSSData {
  encoderID: string;
  simpleName: string;
  streamName?: string;
  online: boolean;
  streamInfo: EncoderSampleData | null;
  lastCommunication?: string;
  clientID?: string;
}

/**
 * Encoder event type
 */
export type EncoderWSSEventType = 'connect' | 'disconnect';

/**
 * Processed encoder WSS event (after worker filtering)
 */
export interface EncoderWSSEvent {
  encoderID: string;
  eventType: EncoderWSSEventType;
  online: boolean;
  streamInfo: EncoderSampleData | null;
  simpleName: string;
  clientID: string;
  timestamp: number;
}

// ============================================================================
// Scaledrone Connection Types
// ============================================================================

/**
 * Connection status for Scaledrone WebSocket
 */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Message types received from the Scaledrone worker
 */
export type ScaledroneWorkerMessage =
  | { type: 'event_status'; clientID: string; eventID: string; status: string; timestamp: number; raw: unknown }
  | { type: 'transcode_job'; jobID: string; clientID: string; progress: number; status: string; timestamp: number; raw: unknown; assetID?: string; eventID?: string }
  | { type: 'encoder_wss'; encoderID: string; eventType: EncoderWSSEventType; online: boolean; streamInfo: unknown; simpleName: string; clientID: string; timestamp: number; raw: unknown }
  | { type: 'batch'; updates: ScaledroneWorkerMessage[] };

/**
 * Message types sent to the Scaledrone worker
 */
export type MainToWorkerMessage =
  | { type: 'process'; clientID: string; message: unknown }
  | { type: 'flush' };
